/**
 * The Powersensor tab on the Reports page: home charging measured by Powersensor, per driver
 * and month, week, day or hour. Powersensor is being retired; this file goes with it.
 */
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Download, Printer } from 'lucide-react';
import { useState } from 'react';
import { get } from '../../api/client';
import { useMe } from '../../api/auth';
import { useDrivers } from '../../api/drivers';
import { useOrganisations } from '../../api/organisations';
import { usePermission } from '../../auth/permissions';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState } from '../../components/EmptyState';
import { Stat, StatStrip } from '../../components/StatStrip';
import { formatAgo, formatDateTime } from '../../lib/format';
import { colours } from '../../theme';

const GROUPS = [
  { value: 'month', label: 'By month' },
  { value: 'week', label: 'By week' },
  { value: 'day', label: 'By day' },
  { value: 'hour', label: 'By hour' },
] as const;
type Group = (typeof GROUPS)[number]['value'];

interface Row {
  period: string;
  driverId: string;
  driverName: string;
  organisationName: string;
  applianceName: string | null;
  evKwh: number;
  hoursCharging: number;
  averageKw: number | null;
  rate: number | null;
  reimbursement: number;
  co2Kg: number;
  readings: number;
  /** Admins only. */
  outsideThreshold?: number;
}
interface Report {
  rows: Row[];
  total: number;
  totals: { drivers: number; evKwh: number; hoursCharging: number; reimbursement: number; co2Kg: number; readings: number; outsideThreshold?: number; latestReadingAt: string | null };
  thresholdKw: { low: number; high: number };
}

const monthYear = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const dayFmt = new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
const fixed = (v: number, places: number) => v.toLocaleString('en-AU', { minimumFractionDigits: places, maximumFractionDigits: places });
const money = (v: number) => `$ ${fixed(v, 2)}`;
const num = (v: string, bold = false) => <Typography className="num" sx={{ fontWeight: bold ? 500 : 400, whiteSpace: 'nowrap' }}>{v}</Typography>;

function periodLabel(p: string, group: Group): string {
  const d = new Date(`${p.slice(0, 10)}T00:00:00Z`);
  if (group === 'month') return monthYear.format(d);
  if (group === 'week') return `Week of ${dayFmt.format(d)}`;
  if (group === 'day') return dayFmt.format(d);
  return `${dayFmt.format(d)}, ${p.slice(11, 16)}`;
}

/** Powersensor sends a reading every 5 minutes: 288 a day. */
const READINGS_PER_DAY = 288;
/** Fewer than this share of the expected records shows in red: the account is missing some. */
const RECORDS_SHORT = 0.9;

const addDays = (ymd: string, n: number) => new Date(Date.parse(`${ymd}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
const localToday = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};

/**
 * About how many readings a row's period should have: 288 a day (12 an hour), for the days of
 * the period inside the report's dates, and only up to now for today.
 */
function expectedReadings(period: string, group: Group, from: string, to: string): number {
  const now = new Date();
  const minutesToday = now.getHours() * 60 + now.getMinutes();
  const today = localToday();
  const start = period.slice(0, 10);
  if (group === 'hour') {
    if (start > today) return 0;
    if (start < today) return 12;
    const hour = Number(period.slice(11, 13));
    return hour < now.getHours() ? 12 : hour === now.getHours() ? Math.floor(now.getMinutes() / 5) : 0;
  }
  const monthEnd = new Date(Date.UTC(Number(start.slice(0, 4)), Number(start.slice(5, 7)), 0)).toISOString().slice(0, 10);
  const end = group === 'day' ? start : group === 'week' ? addDays(start, 6) : monthEnd;
  let total = 0;
  for (let d = start < from ? from : start; d <= end && d <= to; d = addDays(d, 1)) {
    if (d > today) break;
    total += d === today ? Math.floor(minutesToday / 5) : READINGS_PER_DAY;
  }
  return total;
}

/** "13h 35m", "49m". */
function hoursMinutes(hours: number): string {
  const m = Math.round(hours * 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

export function PowersensorReport({ from, to, tz, organisationId, canExport }: { from: string; to: string; tz: string; organisationId: string; canExport: boolean }) {
  const [group, setGroup] = useState<Group>('month');
  const [driverId, setDriverId] = useState('');
  const canSeeDrivers = usePermission('drivers.view');
  // The threshold count is for admins checking sensors (the API leaves it out for others).
  const isAdmin = useMe().data?.role === 'admin';
  const drivers = useDrivers('all', organisationId || undefined, canSeeDrivers);
  // Only drivers of organisations with Powersensor turned on.
  const orgs = useOrganisations();
  const powersensorOrgs = new Set((orgs.data ?? []).filter((o) => o.powersensorEnabled).map((o) => o.id));
  const driverChoices = (drivers.data ?? []).filter((d) => powersensorOrgs.has(d.organisationId));
  const search = new URLSearchParams({ from, to, tz, group, ...(organisationId ? { organisationId } : {}), ...(driverId ? { driverId } : {}) }).toString();
  const report = useQuery({
    queryKey: ['powersensor', 'report', search],
    queryFn: () => get<Report>(`/powersensor/report?${search}`),
    placeholderData: keepPreviousData,
    enabled: from <= to,
  });
  const t = report.data?.totals;
  const th = report.data?.thresholdKw ?? { low: 0, high: 24 };

  const allColumns: Column<Row>[] = [
    { key: 'period', header: GROUPS.find((g) => g.value === group)!.label.replace('By ', '').replace(/^\w/, (c) => c.toUpperCase()), sortValue: (r) => r.period, render: (r) => num(periodLabel(r.period, group)) },
    {
      key: 'driver',
      header: 'Driver',
      sortValue: (r) => r.driverName.toLowerCase(),
      render: (r) => (
        <Box>
          <Typography sx={{ fontWeight: 500 }}>{r.driverName}</Typography>
          <Typography variant="body2" color="textSecondary">{[r.organisationName, r.applianceName].filter(Boolean).join(' · ')}</Typography>
        </Box>
      ),
    },
    { key: 'kwh', header: 'kWh', align: 'right', sortValue: (r) => r.evKwh, render: (r) => num(fixed(r.evKwh, group === 'month' ? 1 : 3), true) },
    { key: 'hours', header: 'Charging', align: 'right', hideBelow: 'sm', sortValue: (r) => r.hoursCharging, render: (r) => num(r.hoursCharging ? hoursMinutes(r.hoursCharging) : '—') },
    { key: 'kw', header: 'Average kW', align: 'right', hideBelow: 'md', sortValue: (r) => r.averageKw ?? 0, render: (r) => num(r.averageKw == null ? '—' : fixed(r.averageKw, 1)) },
    { key: 'reimb', header: 'Reimbursement', align: 'right', hideBelow: 'sm', sortValue: (r) => r.reimbursement, render: (r) => num(r.rate ? money(r.reimbursement) : '—') },
    { key: 'co2', header: 'CO₂, kg', align: 'right', hideBelow: 'md', sortValue: (r) => r.co2Kg, render: (r) => num(fixed(r.co2Kg, 2)) },
    {
      key: 'records',
      header: 'Records',
      align: 'right',
      hideBelow: 'md',
      sortValue: (r) => r.readings,
      render: (r) => {
        const expected = expectedReadings(r.period, group, from, to);
        const short = expected > 0 && r.readings < expected * RECORDS_SHORT;
        return (
          <Typography
            className="num"
            title={expected ? `${r.readings.toLocaleString('en-AU')} of about ${expected.toLocaleString('en-AU')} expected (one every 5 minutes)` : undefined}
            sx={{ color: short ? colours.faultRed : undefined, whiteSpace: 'nowrap' }}
          >
            {r.readings.toLocaleString('en-AU')}
            {expected > 0 && <Typography component="span" variant="body2" color="textSecondary"> / {expected.toLocaleString('en-AU')}</Typography>}
          </Typography>
        );
      },
    },
    {
      key: 'outside',
      header: 'Outside threshold',
      align: 'right',
      hideBelow: 'md',
      sortValue: (r) => r.outsideThreshold ?? 0,
      render: (r) => (
        <Typography className="num" sx={{ color: r.outsideThreshold ? colours.faultRed : 'text.secondary', whiteSpace: 'nowrap' }}>
          {r.outsideThreshold ? `${r.outsideThreshold} of ${r.readings}` : '—'}
        </Typography>
      ),
    },
  ];
  // Records and the threshold count are for admins checking sensors.
  const columns = isAdmin ? allColumns : allColumns.filter((c) => c.key !== 'outside' && c.key !== 'records');

  const exportUrl = (format: 'xlsx' | 'csv') => `/api/powersensor/report/export?${search}&format=${format}`;

  return (
    <>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} className="no-print" sx={{ mb: 3, alignItems: { md: 'center' }, flexWrap: 'wrap', rowGap: 1.5 }}>
        <TextField select size="small" value={group} onChange={(e) => setGroup(e.target.value as Group)} sx={{ width: { xs: '100%', md: 160 } }} slotProps={{ htmlInput: { 'aria-label': 'Group' } }}>
          {GROUPS.map((g) => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
        </TextField>
        {canSeeDrivers && (
          <TextField select size="small" value={driverId} onChange={(e) => setDriverId(e.target.value)} sx={{ width: { xs: '100%', md: 260 } }} slotProps={{ htmlInput: { 'aria-label': 'Driver' }, select: { displayEmpty: true } }}>
            <MenuItem value="">All drivers</MenuItem>
            {driverChoices.slice().sort((a, b) => a.name.localeCompare(b.name)).map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
        )}
        {canExport && (
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', ml: { md: 'auto !important' } }}>
            <Button component="a" href={exportUrl('xlsx')} startIcon={<Download size={20} strokeWidth={1.75} />} disabled={!report.data?.total}>Excel</Button>
            <Button component="a" href={exportUrl('csv')} disabled={!report.data?.total}>CSV</Button>
            <Button startIcon={<Printer size={20} strokeWidth={1.75} />} onClick={() => window.print()} disabled={!report.data?.total}>Print or PDF</Button>
          </Box>
        )}
      </Stack>

      <StatStrip columns={4}>
        <Stat label="Home charging" value={t ? Number(t.evKwh.toFixed(1)) : null} unit="kWh" sub={t ? `${t.drivers} ${t.drivers === 1 ? 'driver' : 'drivers'}, ${hoursMinutes(t.hoursCharging)} charging` : ' '} />
        <Stat label="Reimbursement" value={t ? money(t.reimbursement) : null} sub="At each driver's rate" />
        <Stat label="CO₂ avoided, estimate" value={t ? t.co2Kg : null} unit="kg" sub="Against a petrol car" />
        {isAdmin ? (
          <Stat
            tone={t?.outsideThreshold ? 'fault' : undefined}
            label="Outside threshold"
            value={t ? (t.outsideThreshold ?? 0) : null}
            sub={t ? `of ${t.readings.toLocaleString('en-AU')} readings, counted as 0 kWh` : ' '}
          />
        ) : (
          <Stat label="Average charging speed" value={t ? (t.hoursCharging ? Number((t.evKwh / t.hoursCharging).toFixed(1)) : '—') : null} unit={t?.hoursCharging ? 'kW' : undefined} sub="While the car was charging" />
        )}
      </StatStrip>

      {from > to ? (
        <EmptyState title="Check the dates">The start date is after the end date.</EmptyState>
      ) : (
        <DataTable
          rows={report.data?.rows}
          columns={columns}
          rowKey={(r) => `${r.period}-${r.driverId}`}
          loading={report.isPending}
          error={report.error}
          onRetry={() => void report.refetch()}
          empty={<EmptyState title="No Powersensor readings in this range">Try a longer date range, another organisation or driver.</EmptyState>}
          label="Powersensor"
          pageSize={50}
        />
      )}
      <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
        From the charger appliance chosen on each driver's Powersensor tab. A 5-minute reading counts only if the charger drew between {th.low} and {th.high} kW;
        otherwise it counts as 0 kWh. Days and hours are in each Powersensor account's time zone.
        {t?.latestReadingAt && ` Latest reading: ${formatDateTime(t.latestReadingAt)} (${formatAgo(t.latestReadingAt)}).`}
      </Typography>
    </>
  );
}
